import React, { useDebugValue, useEffect, useState } from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Label,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
  Brush,
  Tooltip,
} from 'recharts';
import dayjs from 'dayjs';
import {
  TrendGraphPageDataType,
  TrendManagementSettingType,
  GetGraphDataListsType,
  GraphDataListsType,
} from './TrendGraph';
import * as Constant from '../../../Constant';
import { ToValueRoundInternal, ToPowIndex, ToValueMaxRound, ToValueMinRound } from '../../../function/CalculationModel';

import { GetUnitWithClc } from '../GroupSettings/TrendSetting/TrendSetting';

const useStyles = makeStyles(
  createStyles({
    customTooltip: {
      background: '#FFFFEE',
      fontSize: '14px',
      padding: '10px 10px 0px 20px',
    },
  })
);

export type GraphDispSetting = {
  yMin: number;
  yMax: number;
};

type GraphData = {
  lineNo: number;
  lineName: string;
  points: GraphPoint[];
  valueUpper: number;
  valueUpperLimit: number;
  selectFlg: boolean;
  infoColor: string;
  yMax: number;
  yMin: number;
};

type GraphPoint = {
  datetime: Date;
  value: number;
};

type GraphPointEx = {
  datetime: Date;
  value1: number;
  value2: number;
  value3: number;
  value4: number;
  value5: number;
  value6: number;
  value7: number;
  value8: number;
};

type GraphSetting = {
  upper: number;
  upperLimit: number;
  yMin: number;
  yMax: number;
  backColor: string;
  memoryFontColor: string;
  yUnit: string;
  xUnit: string;
  yUnitColor: string;
  xUnitColor: string;
  gridColor: string;
  xticks: string[];
};

const convertGraphPoint = (data: GraphData[]) => {
  const result: GraphPointEx[] = [];
  data[0]?.points.map((r: GraphPoint) => {
    result.push({
      datetime: r.datetime,
      value1: 0,
      value2: 0,
      value3: 0,
      value4: 0,
      value5: 0,
      value6: 0,
      value7: 0,
      value8: 0,
    });
  });
  data.map((row, index) => {
    //console.debug(['row , index ', row, index]);
    row.points.map((row2, index2) => {
      if (index == 0) {
        //console.debug(['row2 , index ', row2, index]);
        result[index2].value1 = row2.value;
      }
      if (index == 1) {
        result[index2].value2 = row2.value;
      }
      if (index == 2) {
        result[index2].value3 = row2.value;
      }
      if (index == 3) {
        result[index2].value4 = row2.value;
      }
      if (index == 4) {
        result[index2].value5 = row2.value;
      }
      if (index == 5) {
        result[index2].value6 = row2.value;
      }
      if (index == 6) {
        result[index2].value7 = row2.value;
      }
      if (index == 7) {
        result[index2].value8 = row2.value;
      }
    });
  });
  console.debug(['graphPointEx', result]);
  return result;
};

const CreateGData = (rows: GraphDataListsType): GraphData[] => {
  var ret: GraphData[] = [];
  for (var i in rows.GraphDataList) {
    var item: GraphData = {
      lineNo: Number(i),
      valueUpper: rows.GraphDataList[i].UpperValue,
      valueUpperLimit: rows.GraphDataList[i].UpperLimitValue,
      selectFlg: true,
      points: [],
      infoColor: '',
      lineName: '',
      yMax: 0,
      yMin: 0,
    };

    for (var j in rows.GraphDataList[i].ValueDateTime) {
      item.points.push({ datetime: rows.GraphDataList[i].ValueDateTime[j], value: rows.GraphDataList[i].Value[j] });
    }
    var max = Math.round(Math.max(...item.points.map((p) => p.value)));
    var min = Math.min(...item.points.map((p) => p.value));

    var maxA: number;
    var minA: number;
    maxA = ToValueMaxRound(max);
    minA = ToValueMinRound(min);
    item.yMax = maxA;
    item.yMin = minA;
    ret.push(item);
  }

  return ret;
};

type Props = {
  onChagneYMinMax: (min: number, max: number) => void;
  trendSettingList: TrendManagementSettingType[];
  targetDay: string;
  chartContainerStyle: string;
  onChengeDateTime: (value: string) => void;
  graphDispSetting: GraphDispSetting;
  trendGraphPageData: TrendGraphPageDataType;
};

const TrendGraphChart: React.FC<Props> = (props) => {
  const classes = useStyles();
  const [graphData, setGraphData] = useState<GraphData[]>([]);

  const [graphSetting, setGraphSetting] = useState<GraphSetting>({
    upper: 0,
    upperLimit: 0,
    yMax: 0,
    yMin: 0,
    backColor: Constant.GraphThemeLight.BACK_COLOR,
    memoryFontColor: Constant.GraphThemeLight.MEMORY_FONT_COLOR,
    yUnit: '',
    xUnit: '',
    yUnitColor: Constant.GraphThemeLight.UNIT_COLOR,
    xUnitColor: Constant.GraphThemeLight.UNIT_COLOR,
    gridColor: Constant.GraphThemeLight.GRID_COLOR,
    xticks: [],
  });
  const chartBrushMin = React.useRef<number>(334);
  const chartBrushMax = React.useRef<number>(364);
  const setData = () => {
    if (props.trendSettingList != null) {
      var target = props.trendSettingList.find((item) => item.IsRowSelect);

      console.debug(['■■　target', target]);

      graphSetting.xticks = [''];
      if (target != null) {
        graphSetting.upper = Number(target.TrendValueUpper);
        graphSetting.upperLimit = Number(target.TrendValueUpperLimit);
        graphSetting.yUnit = GetUnitWithClc(
          Number(target.PLCNo),
          target.UnitSymbol,
          target.Clc,
          target.MeasureKindNo,
          target.Kind
        );

        for (let i = 0; i < 12; i++) {
          graphSetting.xticks.push(dayjs(props.targetDay).add(i, 'month').format('YYYY-MM-DDT00:00:00'));
        }
      }
      for (var i in graphData) {
        if (props.trendSettingList[i].PLCNo != '0') {
          graphData[i].selectFlg = props.trendSettingList[i].IsShow;
          graphData[i].infoColor = props.trendSettingList[i].LineColor;
          graphData[i].lineName = props.trendSettingList[i].ItemName;
        }
      }
    }

    //setGraphData({ ...graphData });
  };

  setData();

  useEffect(() => {
    const toDay: string = dayjs(props.targetDay).add(1, 'year').format('YYYY-MM-DD');
    GetGraphDataListsType(props.trendGraphPageData.selectGroupNo.toString(), props.targetDay, toDay).then((result) => {
      console.debug(['GetGraphDataListsType result', result]);
      var tList = CreateGData(result);

      //うるう年問題対応
      if (tList.length !== 0) {
        if (tList[0].points.length == 366) {
          if (chartBrushMax.current == 364) {
            chartBrushMax.current = 365;
          }
        } else if (tList[0].points.length == 365) {
          if (chartBrushMax.current == 365) {
            chartBrushMax.current = 364;
          }
        }
      }

      setGraphData(tList);

      var showList = tList.filter((item) => item.selectFlg);
      //Y軸取得;
      const yMin = showList == null ? 0 : Math.min(...showList.map((p) => p.yMin));
      const yMax = showList == null ? 0 : Math.max(...showList.map((p) => p.yMax));

      console.debug(['showList, yMin, yMax, siyMax', showList, yMin, yMax, yMax]);
      props.onChagneYMinMax(yMin, yMax);
    });
  }, [props.trendGraphPageData]);

  //Y軸更新
  useEffect(() => {
    var yMax = props.graphDispSetting.yMax;
    var yMin = props.graphDispSetting.yMin;

    setGraphSetting({ ...graphSetting, yMax: yMax, yMin: yMin });
    console.debug(['★★yMin, yMax★★', yMin, yMax]);
  }, [props.graphDispSetting]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    //console.debug(['payload', payload]);
    if (active) {
      return (
        <div className={classes.customTooltip}>
          <div className="label">{dayjs(label).format('YY/MM/DD')}</div>
          {payload?.map((r: any, index: any) => {
            return (
              <div style={{ color: r.color }}>
                {r.name} : {Number(r.value).toFixed(3)}
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  type BrushStartEndIndex = {
    startIndex?: number;
    endIndex?: number;
  };
  //allowDuplicatedCategory 重複したメモリを重ねる
  return (
    <ResponsiveContainer className={props.chartContainerStyle} width="99%">
      <LineChart
        style={{ background: graphSetting.backColor }}
        width={500}
        height={400}
        data={convertGraphPoint(graphData)}
        margin={{
          top: 10,
          right: 30,
          left: 1,
          bottom: 0,
        }}
        onClick={(x: any) => {
          if (x !== null) {
            props.onChengeDateTime(x?.activeLabel);
          }
        }}
      >
        <CartesianGrid stroke={graphSetting.gridColor} strokeDasharray="3 3" />
        <XAxis
          dataKey="datetime"
          type="category"
          allowDuplicatedCategory={false}
          stroke={graphSetting.memoryFontColor}
          tickFormatter={(r) => {
            return r === 0 || r === 'auto' || dayjs(r).isValid() === false ? '' : dayjs(r).format('YY/MM/DD');
          }}
          height={40}
          tick={{ dy: 8, fontSize: 11 }}
          padding={{ right: 0, left: 0 }}
        />

        <YAxis
          stroke={graphSetting.memoryFontColor}
          allowDataOverflow={true}
          domain={[graphSetting.yMin, graphSetting.yMax]}
          tickFormatter={(props) => props.toFixed(2)}
          width={100}
        >
          <Label angle={270} position="insideLeft" style={{ textAnchor: 'middle', padding: 0 }}>
            {graphSetting.yUnit}
          </Label>
        </YAxis>

        <Brush
          className="TimeLineChart-brush"
          dataKey="datetime"
          stroke="#8884d8"
          startIndex={chartBrushMin.current}
          endIndex={chartBrushMax.current}
          height={30}
          tickFormatter={(props) => dayjs(props).format('YY/MM/DD')}
          onChange={(e: any) => {
            chartBrushMin.current = e.startIndex ?? 0;
            chartBrushMax.current = e.endIndex ?? 0;
            //return console.debug(['TimeLineChart-brush', e]);
            return '';
          }}
        />

        <Tooltip content={<CustomTooltip />} />
        <ReferenceLine y={graphSetting.upper} stroke={Constant.LIGHT_FAULT_LINE_COLOR} alwaysShow={false} />
        <ReferenceLine y={graphSetting.upperLimit} stroke={Constant.SERIOUS_FAULT_LINE_COLOR} alwaysShow={false} />
        {graphData?.map((row, index) => (
          <Line
            key={index}
            dataKey={'value' + String(index + 1)}
            stroke={row.infoColor}
            strokeWidth={2}
            dot={{ r: 0.2 }}
            name={row.lineName.toString()}
            hide={!row.selectFlg}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
export default TrendGraphChart;
